import type { AsyncSequence } from "../types/AsyncSequence"

import { Channel } from "../classes/Channel"
import { Map } from "../functions/Map"
import { ProcessIterable } from "./ProcessIterable"
import { SafePromise } from "./SafePromise"

/**
 * Maps over async sequences with concurrent processing.
 * Unlike MapAsync, this processes all items concurrently and yields results as they complete.
 * The output order depends on completion time, not input order.
 *
 * @param func - Transformation function to apply to each item
 * @param arrays - One or more async sequences to map over
 * @returns An async generator yielding transformed values as they complete
 *
 * @example
 * ```ts
 * import { MapAsyncConcurrent } from "@pdfking/tfunc"
 *
 * async function* generator1() {
 *   await Sleep(30);
 *   yield 1;
 *   await Sleep(30);
 *   yield 2;
 * }
 *
 * async function* generator2() {
 *   await Sleep(50);
 *   yield 10;
 * }
 *
 * const result = MapAsyncConcurrent(x => x * 2, generator1(), generator2());
 * for await (const value of result) {
 *   console.log(value); // 2, 20, 4 (order based on completion time)
 * }
 * ```
 */
export const MapAsyncConcurrent = <T, N>(
  func: (item: T, index: number) => N | Promise<N>,
  ...arrays: AsyncSequence<T>[]
): AsyncGenerator<N> => {
  const { push, stop, stream } = Channel<N>()

  let seen = 0
  let done = 0
  let completed = false

  const final_stop = () => (completed && done === seen ? stop(undefined) : null)

  const promises = Map(
    iterable =>
      ProcessIterable(
        iterable,
        s =>
          void SafePromise(func(s, seen++) as Promise<N>).then(
            ({ error, value }) => {
              done++
              if (error) {
                stop(error)
              } else {
                push(value)
                final_stop()
              }
            }
          ),
        (error?: Error) => (error ? stop(error) : null)
      ),
    arrays
  )

  void Promise.all(promises).then(() => {
    completed = true
    final_stop()
  })

  return stream
}
